import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { ArrowRight, Truck, MapPin, Package, Calendar, ExternalLink, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { StatusBadge } from '../ui-kit/StatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OperationDetailPanel
 * --------------------
 * Slide-over panel pour afficher les détails d'une opération
 * sans quitter le dashboard. Aller plus loin → page complète.
 */
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (e) {
    return '—';
  }
};

const fmtNum = (n) => {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('fr-FR').format(n);
};

export const OperationDetailPanel = ({ operationId, open, onClose }) => {
  const navigate = useNavigate();
  const [op, setOp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!operationId || !open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/transport-requests/${operationId}`)
      .then((res) => { if (!cancelled) setOp(res.data); })
      .catch((e) => {
        if (!cancelled) {
          console.error('Op detail error:', e);
          setError("Impossible de charger les détails");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [operationId, open]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="operation-detail-panel"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-emerald-600" />
            Détails de l'opération
          </SheetTitle>
        </SheetHeader>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-10 text-sm text-slate-500">{error}</div>
        )}

        {op && !loading && (
          <div className="space-y-5">
            {/* Identifier + statut */}
            <div className="flex items-start justify-between gap-3 pb-3 border-b border-slate-100">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">N° Opération</p>
                <p className="text-base font-bold text-slate-900 truncate">
                  {op.operationNumber || `Op #${op.id?.slice(0, 8)}`}
                </p>
              </div>
              <StatusBadge status={op.status} />
            </div>

            {op.externalRef && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="panel-external-ref">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">
                  {op.externalRefLabel || 'Référence client'}
                </p>
                <p className="text-sm font-mono font-semibold text-emerald-800 truncate">{op.externalRef}</p>
              </div>
            )}

            {/* Trajet */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Trajet</p>
              <div className="space-y-2">
                <div className="flex items-start gap-2.5">
                  <MapPin className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-slate-500">Départ</p>
                    <p className="text-sm font-medium text-slate-900 truncate">{op.departure || '—'}</p>
                  </div>
                </div>
                <div className="ml-1.5 w-px h-4 bg-slate-200" />
                <div className="flex items-start gap-2.5">
                  <MapPin className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-slate-500">Arrivée</p>
                    <p className="text-sm font-medium text-slate-900 truncate">{op.arrival || '—'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Infos clés */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
                  <Package className="w-3.5 h-3.5" />
                  <span>Marchandise</span>
                </div>
                <p className="text-sm font-medium text-slate-900 truncate">
                  {op.goodsType || op.merchandise || op.cargo || '—'}
                </p>
                {op.weight && (
                  <p className="text-xs text-slate-500 mt-0.5">{fmtNum(op.weight)} {op.weightUnit || 'kg'}</p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Date</span>
                </div>
                <p className="text-sm font-medium text-slate-900">
                  {fmtDate(op.requestedDate || op.scheduledDate || op.createdAt)}
                </p>
              </div>
            </div>

            {/* Prix estimé */}
            {op.estimatedPrice != null && op.estimatedPrice > 0 && (
              <div className="p-3 rounded-lg border border-emerald-100 bg-emerald-50/50">
                <p className="text-[11px] text-emerald-700 uppercase tracking-wide font-semibold">Prix estimé</p>
                <p className="text-lg font-bold text-emerald-900">{fmtNum(op.estimatedPrice)} FCFA</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-3 border-t border-slate-100">
              <Button
                onClick={() => { onClose(); navigate(`/operations/${op.id}`); }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="open-full-detail-btn"
              >
                Ouvrir la fiche complète
                <ExternalLink className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={onClose}
                className="text-slate-500 hover:text-slate-700"
                data-testid="close-detail-panel-btn"
              >
                Fermer
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default OperationDetailPanel;
